import React, { useState, useMemo, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../../utils/dateUtils';
import { createTruncatedTextForTable } from '../../../../../utils/textUtils';

const CustomerPriceListTable = ({ data, totalRecords, isLoading, onPage, lazyParams, onEdit, resetPagination = false }) => {
    const { th } = useLanguage();
    const safeParams = lazyParams || { page: 1, rows: 10 };

    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    const [currentFirst, setCurrentFirst] = useState(0);

    useEffect(() => {
        if (resetPagination) {
            setCurrentFirst(0);
            setSortField(null);
            setSortOrder(null);
        }
    }, [resetPagination]);

    const handlePage = (event) => {
        setCurrentFirst(event.first);
        onPage(event);
    };

    const onSort = (event) => {
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
    };

    const sortedData = useMemo(() => {
        if (!sortField || !data) return data;

        return [...data].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            let comparison = 0;
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                comparison = aVal.localeCompare(bVal, 'tr', { numeric: true });
            } else if (typeof aVal === 'number' && typeof bVal === 'number') {
                comparison = aVal - bVal;
            } else {
                comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            }

            return sortOrder * comparison;
        });
    }, [data, sortField, sortOrder]);

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2 justify-content-center">
            <i onClick={() => onEdit(rowData)} className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('customerAddressPrice.edit')}></i>
        </div>
    );

    const usageTypeTemplate = (rowData) => (rowData.usageType === 1 ? th('customerAddressPrice.service') : rowData.usageType === 2 ? th('customerAddressPrice.sales') : '');

    const statusBodyTemplate = (rowData) => <span className={`p-badge p-badge-${rowData.isActive ? 'success' : 'danger'}`}>{rowData.isActive ? th('customerAddressPrice.active') : th('customerAddressPrice.inactive')}</span>;

    return (
        <DataTable
            value={sortedData}
            paginator
            lazy
            first={currentFirst}
            rows={safeParams.rows}
            totalRecords={totalRecords}
            loading={isLoading}
            onPage={handlePage}
            onSort={onSort}
            sortField={sortField}
            sortOrder={sortOrder}
            rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
            dataKey="id"
            tableStyle={{ minWidth: '50rem' }}
            showGridlines
            stripedRows
            emptyMessage={th('customerAddressPrice.emptyMessage')}
            removableSort
            sortMode="single"
        >
            <Column body={actionBodyTemplate} style={{ width: '5%' }} align="center" />
                                    <Column field="dealerName" header={th('table.dealerName')} sortable body={(rowData) => createTruncatedTextForTable(rowData.dealerName, 40)} />
            <Column field="dealerAddress" header={th('table.address')} sortable body={(rowData) => createTruncatedTextForTable(rowData.dealerAddress, 40)} />
            <Column field="listHeaderName" header={th('customerAddressPrice.priceList')} sortable />
            <Column field="usageType" header={th('customerAddressPrice.usageType')} body={usageTypeTemplate} sortable />
            <Column field="contractStartDate" header={th('table.contractStartDate')} body={(row) => formatDateOnly(row?.contractStartDate)} sortable />
            <Column field="contractEndDate" header={th('table.contractEndDate')} body={(row) => formatDateOnly(row?.contractEndDate)} sortable />
            <Column field="isActive" header={th('table.status')} body={statusBodyTemplate} sortable />
        </DataTable>
    );
};

export default CustomerPriceListTable;
